import { memo, useState, type PropsWithChildren } from 'react';
import ReactDOM from 'react-dom';
import { Form } from 'react-final-form';

import { usePopperTooltip } from 'shared/ui/Popper';
import { BaseFileTypeDto } from '@eva/types/dto';

import { FilesFilterValues } from '../types';

// eslint-disable-next-line import/no-cycle
import FormFilesFilter from './FormFilesFilter';

interface FilesFilterProps<FT extends BaseFileTypeDto> {
  fileTypes: FT[];
  initialValues: FilesFilterValues;
  onSubmit: (filterValues: FilesFilterValues) => void;
  onClearFilter: () => void;
}

const FilesFilter = <FT extends BaseFileTypeDto>({
  fileTypes,
  initialValues,
  children,
  onSubmit,
  onClearFilter,
}: PropsWithChildren<FilesFilterProps<FT>>) => {
  const [visible, setVisible] = useState(false);

  const { getArrowProps, getTooltipProps, setTooltipRef, setTriggerRef } = usePopperTooltip({
    visible,
    onVisibleChange: setVisible,
    closeOnOutsideClick: true,
    interactive: true,
    placement: 'bottom',
    trigger: 'click',
    mutationObserverOptions: null,
    offset: [0, 21],
  });

  return (
    <div className="tooltip-wrapper-box">
      <span ref={setTriggerRef} onClick={() => setVisible(true)}>
        {children}
      </span>

      {visible &&
        ReactDOM.createPortal(
          <div
            ref={setTooltipRef}
            {...getTooltipProps({
              className: 'tooltip-container filter-popover',
            })}
            onClick={(evt) => evt.stopPropagation()}
          >
            <Form
              onSubmit={onSubmit}
              initialValues={initialValues}
              render={({ handleSubmit, submitting, form, values }) => (
                <form onSubmit={handleSubmit}>
                  <FormFilesFilter fileTypes={fileTypes} />

                  <div className="popover-btn-line row text-right margin-top">
                    <button
                      type="button"
                      disabled={submitting}
                      className="btn btn-tool-box btn-sm pull-left"
                      onClick={() => {
                        form.reset();
                        onClearFilter();
                      }}
                      style={{
                        paddingLeft: 0,
                      }}
                    >
                      <i className="lnr lnr-cross" />
                      &nbsp;{translate('Clear filter')}
                    </button>

                    <button
                      type="button"
                      disabled={submitting}
                      className="btn btn-success btn-sm"
                      onClick={() => {
                        setVisible(false);
                        onSubmit(values);
                      }}
                    >
                      {translate('Apply')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setVisible(false)}
                      className="btn btn-default btn-sm margin-left"
                    >
                      {translate('Cancel')}
                    </button>
                  </div>
                </form>
              )}
            />
            <div {...getArrowProps({ className: 'tooltip-arrow' })} />
          </div>,
          document.body,
        )}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(FilesFilter);
